import React, { useState } from 'react';

/**
 * Displays Terminal Arrival Altitude calculation results returned by
 * POST /api/taa-calculation.
 *
 * Shows a per-sector obstacle table analogous to the MSA result panel,
 * including OCA, clearance, critical flag, and the computed TAA altitude.
 *
 * Props:
 *   taaAnalysis       – response from POST /api/taa-calculation
 *                       { summary, results: [...] }
 *   taaError          – optional error string
 *   showTaaSectors    – boolean controlling map overlay visibility
 *   setShowTaaSectors – setter for the above
 */
export default function TaaResultsPanel({ taaAnalysis, taaError, showTaaSectors, setShowTaaSectors }) {
  const [sortConfig, setSortConfig] = useState({ key: 'ocaM', direction: 'desc' });

  if (!taaAnalysis && !taaError) return null;

  const tdStyle = { padding: 6, border: '1px solid #ddd', whiteSpace: 'nowrap' };

  const handleSort = (key) =>
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));

  const renderSortIndicator = (key) =>
    sortConfig.key === key
      ? <span style={{ marginLeft: 4 }}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
      : <span style={{ opacity: 0.3, marginLeft: 4 }}>▼</span>;

  const thStyle = (key) => ({
    padding: 6,
    border: '1px solid #80b3d4',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    background: sortConfig.key === key ? '#b3d4e8' : '#d6eaf8',
    cursor: 'pointer',
    userSelect: 'none',
  });

  const sortRows = (rows) => {
    const key = sortConfig.key;
    const dir = sortConfig.direction;
    return [...rows].sort((a, b) => {
      let av = a[key], bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return dir === 'asc' ? 1 : -1;
      if (bv == null) return dir === 'asc' ? -1 : 1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return dir === 'asc' ? av - bv : bv - av;
      }
      av = String(av); bv = String(bv);
      return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  };

  const renderObstacleTable = (rows, sectorIdx) => {
    if (!rows || rows.length === 0) return null;
    const sorted = sortRows(rows.map((r, i) => ({ ...r, _taaId: `taa-${sectorIdx}-${i}` })));
    return (
      <div style={{ overflowX: 'auto', marginTop: 8 }}>
        <table style={{ borderCollapse: 'collapse', fontSize: '12px', minWidth: '900px', width: '100%' }}>
          <thead>
            <tr style={{ background: '#d6eaf8' }}>
              <th style={thStyle('obstacleName')}   onClick={() => handleSort('obstacleName')}>Obstacle Name{renderSortIndicator('obstacleName')}</th>
              <th style={thStyle('obstacleAltM')}   onClick={() => handleSort('obstacleAltM')}>Alt (m){renderSortIndicator('obstacleAltM')}</th>
              <th style={thStyle('obstacleAltFt')}  onClick={() => handleSort('obstacleAltFt')}>Alt (ft){renderSortIndicator('obstacleAltFt')}</th>
              <th style={thStyle('mocM')}            onClick={() => handleSort('mocM')}>MOC (m){renderSortIndicator('mocM')}</th>
              <th style={thStyle('mocFt')}           onClick={() => handleSort('mocFt')}>MOC (ft){renderSortIndicator('mocFt')}</th>
              <th style={thStyle('ocaM')}            onClick={() => handleSort('ocaM')}>OCA (m){renderSortIndicator('ocaM')}</th>
              <th style={thStyle('ocaFt')}           onClick={() => handleSort('ocaFt')}>OCA (ft){renderSortIndicator('ocaFt')}</th>
              <th style={thStyle('roundedAltM')}     onClick={() => handleSort('roundedAltM')}>TAA (m){renderSortIndicator('roundedAltM')}</th>
              <th style={thStyle('roundedAltFt')}    onClick={() => handleSort('roundedAltFt')}>TAA (ft){renderSortIndicator('roundedAltFt')}</th>
              <th style={thStyle('distFromCentreM')} onClick={() => handleSort('distFromCentreM')}>Dist from IF (m){renderSortIndicator('distFromCentreM')}</th>
              <th style={thStyle('clearanceM')}      onClick={() => handleSort('clearanceM')}>Clearance (m){renderSortIndicator('clearanceM')}</th>
              <th style={thStyle('clearanceFt')}     onClick={() => handleSort('clearanceFt')}>Clearance (ft){renderSortIndicator('clearanceFt')}</th>
              <th style={thStyle('isCritical')}      onClick={() => handleSort('isCritical')}>Critical{renderSortIndicator('isCritical')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, rowIdx) => {
              const isControlling = rowIdx === 0;
              const baseBg = isControlling ? '#c8dff0' : 'white';
              return (
                <tr
                  key={row._taaId}
                  style={{ background: baseBg }}
                  onMouseEnter={e => { if (!isControlling) e.currentTarget.style.background = '#eaf4fb'; }}
                  onMouseLeave={e => { if (!isControlling) e.currentTarget.style.background = baseBg; }}
                >
                  <td style={tdStyle}>{row.obstacleName}</td>
                  <td style={tdStyle}>{row.obstacleAltM != null ? row.obstacleAltM : '—'}</td>
                  <td style={tdStyle}>{row.obstacleAltFt != null ? row.obstacleAltFt : '—'}</td>
                  <td style={{ ...tdStyle, color: '#555' }}>{row.mocM != null ? `${row.mocM} m` : '—'}</td>
                  <td style={{ ...tdStyle, color: '#555' }}>{row.mocFt != null ? `${row.mocFt} ft` : '—'}</td>
                  <td style={tdStyle}>{row.ocaM != null ? row.ocaM : '—'}</td>
                  <td style={tdStyle}>{row.ocaFt != null ? row.ocaFt : '—'}</td>
                  <td style={{ ...tdStyle, fontWeight: isControlling ? 'bold' : 'normal', color: '#154360' }}>
                    {row.roundedAltM != null ? row.roundedAltM : '—'}
                  </td>
                  <td style={{ ...tdStyle, fontWeight: isControlling ? 'bold' : 'normal', color: '#154360' }}>
                    {row.roundedAltFt != null ? row.roundedAltFt : '—'}
                  </td>
                  <td style={tdStyle}>{row.distFromCentreM != null ? row.distFromCentreM : '—'}</td>
                  <td style={{ ...tdStyle, color: row.clearanceM != null && row.clearanceM < 0 ? '#d32f2f' : '#2e7d32' }}>
                    {row.clearanceM != null ? row.clearanceM : '—'}
                  </td>
                  <td style={{ ...tdStyle, color: row.clearanceFt != null && row.clearanceFt < 0 ? '#d32f2f' : '#2e7d32' }}>
                    {row.clearanceFt != null ? row.clearanceFt : '—'}
                  </td>
                  <td style={{ ...tdStyle, color: row.isCritical ? '#d32f2f' : 'inherit', fontWeight: row.isCritical ? 'bold' : 'normal' }}>
                    {row.isCritical ? 'Yes' : 'No'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const results = (taaAnalysis?.results || []).filter(r => r.obsRows?.length > 0);
  if (!results.length && !taaError) return null;

  return (
    <div style={{ marginTop: 10, background: '#d6eaf8', padding: 16, borderRadius: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <h3 style={{ marginTop: 0, marginBottom: 0, color: '#154360' }}>
          TAA Calculation Results
        </h3>
        {setShowTaaSectors && (
          <button
            onClick={() => setShowTaaSectors(v => !v)}
            title="Toggle TAA sector polygons on the map"
            style={{
              padding: '3px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
              background: showTaaSectors ? '#154360' : '#d6eaf8',
              color: showTaaSectors ? '#fff' : '#154360',
              border: '1px solid #154360', whiteSpace: 'nowrap',
            }}
          >
            🗺 {showTaaSectors ? 'Hide TAA sectors' : 'Show TAA sectors'}
          </button>
        )}
        {taaAnalysis?.summary && (
          <span style={{ fontSize: 12, color: '#555' }}>
            {taaAnalysis.summary.sectorsEvaluated} sector{taaAnalysis.summary.sectorsEvaluated !== 1 ? 's' : ''} evaluated,&nbsp;
            {taaAnalysis.summary.totalObstacles} obstacle{taaAnalysis.summary.totalObstacles !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {taaError && (
        <div style={{ color: '#b30000', background: '#ffebee', padding: 8, borderRadius: 4, marginBottom: 8 }}>
          <b>TAA Error:</b> {taaError}
        </div>
      )}

      {results.length === 0 ? (
        <p style={{ color: '#555' }}>
          {taaAnalysis?.message || 'No TAA sector configurations found.'}
        </p>
      ) : (
        results.map((result) => {
          const sectorIdx = taaAnalysis.results.indexOf(result);
          const inp = result.input || {};
          const hasCritical = result.criticalCount > 0;
          return (
            <details key={sectorIdx} style={{ marginBottom: 12, background: 'white', padding: 12, borderRadius: 4 }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#154360' }}>
                {[inp.procedure_id, inp.sector_type].filter(Boolean).join(' / ')}
                {[inp.procedure_id, inp.sector_type].some(Boolean) ? ' – ' : ''}
                {inp.airport_name || inp.airport_code || '—'}
                {inp.sector_from_deg != null
                  ? ` (${inp.sector_from_deg}°–${inp.sector_to_deg}°)`
                  : ''}
                {inp.radius_nm != null ? ` / ${inp.radius_nm} NM` : ''}
                {result.taa?.formatted ? (
                  <span style={{ marginLeft: 12, color: '#154360', fontWeight: 'bold' }}>
                    → {result.taa.formatted}
                  </span>
                ) : null}
                {hasCritical
                  ? <span style={{ marginLeft: 10, color: '#d32f2f', fontWeight: 'bold' }}>⚠ Impact ({result.criticalCount} critical)</span>
                  : (result.obsRows?.length > 0
                      ? <span style={{ marginLeft: 10, color: '#2e7d32' }}>✓ No impact</span>
                      : null)
                }
                {inp.error && (
                  <span style={{ color: '#d32f2f', marginLeft: 8 }}>⚠ {inp.error}</span>
                )}
              </summary>

              {/* Input parameters */}
              <div style={{ marginTop: 8, marginBottom: 8, fontSize: '12px', background: '#eaf4fb', padding: 8, borderRadius: 4 }}>
                <b>Input Parameters</b>
                <table style={{ borderCollapse: 'collapse', marginTop: 4, width: '100%' }}>
                  <tbody>
                    <tr>
                      <td style={tdStyle}>Airport</td>
                      <td style={tdStyle}><b>{inp.airport_name || inp.airport_code || '—'}</b></td>
                      <td style={tdStyle}>Procedure</td>
                      <td style={tdStyle}><b>{inp.procedure_id || '—'}</b></td>
                      <td style={tdStyle}>Sector</td>
                      <td style={tdStyle}><b>{inp.sector_type || '—'}</b></td>
                    </tr>
                    <tr>
                      <td style={tdStyle}>IF waypoint</td>
                      <td style={tdStyle}><b>{inp.center_wpt_ident || '—'}</b></td>
                      <td style={tdStyle}>Left WPT</td>
                      <td style={tdStyle}><b>{inp.base_left_wpt_ident || '—'}</b></td>
                      <td style={tdStyle}>Right WPT</td>
                      <td style={tdStyle}><b>{inp.base_right_wpt_ident || '—'}</b></td>
                    </tr>
                    <tr>
                      <td style={tdStyle}>Sector From</td>
                      <td style={tdStyle}><b>{inp.sector_from_deg != null ? `${inp.sector_from_deg}°` : '—'}</b></td>
                      <td style={tdStyle}>Sector To</td>
                      <td style={tdStyle}><b>{inp.sector_to_deg != null ? `${inp.sector_to_deg}°` : '—'}</b></td>
                      <td style={tdStyle}>Radius</td>
                      <td style={tdStyle}><b>{inp.radius_nm != null ? `${inp.radius_nm} NM` : '—'}</b></td>
                    </tr>
                    <tr>
                      <td style={tdStyle}>MOC</td>
                      <td style={tdStyle}><b>{inp.moc != null ? `${inp.moc} ${inp.moc_unit || ''}` : '—'}</b></td>
                      <td style={tdStyle}>Published TAA</td>
                      <td style={{ ...tdStyle, color: '#154360', fontWeight: 'bold' }}>
                        {inp.min_altitude != null ? `${inp.min_altitude} ${inp.altitude_unit || ''}` : '—'}
                      </td>
                      <td style={tdStyle}>Computed TAA</td>
                      <td style={{ ...tdStyle, color: '#154360', fontWeight: 'bold' }}>
                        {result.taa?.formatted || '—'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Obstacle table */}
              <div style={{ marginTop: 8, border: '1px solid #80b3d4', borderRadius: 4, padding: 8 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                  Obstacles inside protected area (sector + 5 NM buffer)
                  <span style={{ marginLeft: 8, fontWeight: 'normal', color: hasCritical ? '#d32f2f' : '#555' }}>
                    ({result.criticalCount || 0} critical)
                  </span>
                </div>
                {result.obsRows && result.obsRows.length > 0
                  ? renderObstacleTable(result.obsRows, sectorIdx)
                  : <p style={{ color: '#666', fontStyle: 'italic', margin: 4 }}>No obstacles within protected area.</p>
                }
              </div>
            </details>
          );
        })
      )}
    </div>
  );
}
